import { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import Loader from "../../../../components/loader";
import { DispatchDetailsFormEntity, GetDispatchDetailsEntity } from "../../../../interface/tbs-tracker/send-tbs";
import { DispatchFromEnum, ModeOfTransportEnum, TBSSelectSizeEnumEntity } from "../../../../utils/enum";
import { ALPHABET_SPACE_PATTERN, INDIAN_MOBILE_NO_PATTERN, NUMBER_ONLY_PATTERN } from "../../../../utils/pattern";
import { APISaveDispatchDetails } from "../../../../service/api-service";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { toast } from "react-toastify";
import { toastOptObj } from "../../../../utils/constants";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { checkValidInputOnKeyDown, handleDownloadFilesFromLinks, removeSpaceOnFirstCharacter } from "../../../../service/shared.service";

interface Props {
    tbsId: string;
    dispatchData: GetDispatchDetailsEntity;
    onNext: () => void;
    onBack: () => void;
}

export const dispatchDDL = [
    {
        label: "Site",
        value: DispatchFromEnum.SITE,
    },
    {
        label: "Factory",
        value: DispatchFromEnum.FACTORY,
    },
    {
        label: "Vendor",
        value: DispatchFromEnum.VENDOR,
    },
];

export const transportDDL = [
    {
        label: "Courier",
        value: ModeOfTransportEnum.COURIER,
    },
    {
        label: "Transport",
        value: ModeOfTransportEnum.TRANSPORT,
    },
    {
        label: "Hand Carry",
        value: ModeOfTransportEnum.HAND_CARRY,
    },
];
interface FilePreviewProps {
    fileName?: string;
    fileUrl?: string;
    onClear?: () => void;
}

const FilePreview = ({
    fileName,
    fileUrl,
    onClear
}: FilePreviewProps) => (
    <div className="form-group">
        <div className="border rounded bg-light px-3 py-1 d-flex align-items-center justify-content-between" style={{ height: "48px" }}>
            <span className="text-truncate small">
                {fileName || "-"}
            </span>

            <div className="d-flex align-items-center gap-1">
                <Button
                    type="button"
                    icon="pi pi-download"
                    text
                    rounded
                    severity="secondary"
                    disabled={!fileUrl}
                    onClick={() =>
                        fileUrl &&
                        handleDownloadFilesFromLinks([fileUrl])
                    }
                />

                <Button
                    type="button"
                    icon="pi pi-times"
                    text
                    rounded
                    severity="danger"
                    onClick={onClear}
                />
            </div>
        </div>
    </div>
);
const DispatchDetailsStep = ({ tbsId, dispatchData, onNext, onBack }: Props) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const [formData, setFormData] = useState<DispatchDetailsFormEntity>({
        selectSize: TBSSelectSizeEnumEntity.APPROX,
        drawingToBeFollowed: true,
        offCutRequired: true,
        uploadDrg: null,
        dispatchDocument: null,
        dispatchFrom: "",
        modeOfTransport: "",
        concernPersonName: "",
        contactNumber: "",
        zoomPicture: "",
        packingPicture: "",
        remarks: "",
    });

    const handleChange = (
        field: keyof DispatchDetailsFormEntity,
        value: any
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateFile = (
        file: File,
        allowedExtensions: string[],
        fileTypeName: string
    ): boolean => {
        const extension = file.name.split(".").pop()?.toLowerCase();
        const maxFileSize = 25 * 1024 * 1024;

        if (!extension || !allowedExtensions.includes(extension)) {
            toast.error(`Only ${fileTypeName} files are allowed.`, { ...toastOptObj });
            return false;
        }

        if (file.size > maxFileSize) {
            toast.error("Maximum file size is 25MB.", { ...toastOptObj });
            return false;
        }

        return true;
    };

    const showError = (field: keyof DispatchDetailsFormEntity) => {
        let message = "";

        switch (field) {
            case "uploadDrg":
                if (formData.drawingToBeFollowed && !formData.uploadDrg) {
                    message = "Please upload DRG!";
                }
                break;

            case "dispatchFrom":
                if (!formData.dispatchFrom) {
                    message = "Please select dispatch from!";
                }
                break;

            case "modeOfTransport":
                if (!formData.modeOfTransport) {
                    message = "Please select mode of transport!";
                }
                break;

            case "concernPersonName":
                if (!formData.concernPersonName.trim()) {
                    message = "Please enter concern person name!";
                }
                break;

            case "contactNumber": {
                const isValid = INDIAN_MOBILE_NO_PATTERN.test(
                    formData.contactNumber
                );

                if (!formData.contactNumber.trim()) {
                    message = "Please enter mobile number";
                } else if (!isValid) {
                    message = "Please enter valid mobile number";
                }
                break;
            }

            case "dispatchDocument":
                if (!formData.dispatchDocument) {
                    message = "Please upload dispatch document!";
                }
                break;

        }

        if (!isSubmitted || !message) return null;

        return (
            <small
                className="text-danger font-weight-500 p-1"
                data-error="step-error"
                data-field={field}
            >
                {message}
            </small>
        );
    };

    const handleSubmitStep = async () => {
        setIsLoading(true);
        try {
            const errorElement = document.querySelector(
                '[data-error="step-error"]'
            );

            if (errorElement) {
                errorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                setIsLoading(false);
                return;
            }

            const reqBody = new FormData();

            reqBody.append("selectSize", formData.selectSize);
            reqBody.append(
                "anyDrawing",
                String(formData.drawingToBeFollowed)
            );
            reqBody.append(
                "offCutRequired",
                String(formData.offCutRequired)
            );
            reqBody.append("dispatchFrom", formData.dispatchFrom);
            reqBody.append("modeOfTransport", formData.modeOfTransport);
            reqBody.append("concernPersonName", formData.concernPersonName);
            reqBody.append("contactNumber", formData.contactNumber);
            reqBody.append("senderRemarks", formData.remarks);

            if (formData.uploadDrg instanceof File) {
                reqBody.append("drawingURL", formData.uploadDrg);
            }

            if (formData.dispatchDocument instanceof File) {
                reqBody.append(
                    "dispatchDocumentURL",
                    formData.dispatchDocument
                );
            }

            const resp = await APISaveDispatchDetails(tbsId, reqBody);
            if (!resp || (resp && (resp.status !== 200 || !resp.data))) { return; }
            const apiResp: APIResponseEntity = resp.data;
            if (apiResp.statusCode == 200 && apiResp.data && apiResp.status) {
                onNext();
            } else {
                toast.error(apiResp.message || apiResp.errorMsg, {
                    ...toastOptObj,
                });
                return;
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!dispatchData) return;
        setFormData({
            selectSize: dispatchData.selectSize || TBSSelectSizeEnumEntity.APPROX,
            drawingToBeFollowed: dispatchData.anyDrawing ?? true,
            offCutRequired: dispatchData.offCutRequired ?? false,
            uploadDrg: dispatchData.drawingURL,
            dispatchDocument: dispatchData.dispatchDocumentURL,
            dispatchFrom: dispatchData.dispatchFrom || "",
            modeOfTransport: dispatchData.modeOfTransport || "",
            concernPersonName: dispatchData.concernPersonName || "",
            contactNumber: dispatchData.contactNumber || "",
            zoomPicture: dispatchData.zoomPicture,
            packingPicture: dispatchData.packingPicture,
            remarks: dispatchData.senderRemarks || "",
        });
    }, [dispatchData]);
    return (
        <>
            <Loader isLoading={isLoading} />
            <div className="card mb-4">
                <div className="card-body py-4">
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>SELECT SIZE <sup className="mandatory">*</sup></label>
                                <div className="d-flex align-items-center mt-2">
                                    <div className="d-flex align-items-center me-4">
                                        <RadioButton
                                            inputId="exactSize"
                                            checked={formData.selectSize === TBSSelectSizeEnumEntity.EXACT}
                                            onChange={() =>
                                                handleChange("selectSize", TBSSelectSizeEnumEntity.EXACT)
                                            }
                                        />
                                        <label htmlFor="exactSize" className="mb-0 ms-2">
                                            Exact Size
                                        </label>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <RadioButton
                                            inputId="approxSize"
                                            checked={formData.selectSize === TBSSelectSizeEnumEntity.APPROX}
                                            onChange={() =>
                                                handleChange("selectSize", TBSSelectSizeEnumEntity.APPROX)
                                            }
                                        />
                                        <label htmlFor="approxSize" className="mb-0 ms-2">
                                            Approx Size
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-4">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>ANY DRAWING TO BE FOLLOWED ?  <sup className="mandatory">*</sup></label>
                                <div className="d-flex align-items-center mt-2">
                                    <div className="d-flex align-items-center me-4">
                                        <RadioButton
                                            inputId="drawingYes"
                                            checked={formData.drawingToBeFollowed}
                                            onChange={() =>
                                                handleChange("drawingToBeFollowed", true)
                                            }
                                        />
                                        <label htmlFor="drawingYes" className="mb-0 ms-2">
                                            Yes
                                        </label>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <RadioButton
                                            inputId="drawingNo"
                                            checked={!formData.drawingToBeFollowed}
                                            onChange={() =>
                                                handleChange("drawingToBeFollowed", false)
                                            }
                                        />
                                        <label htmlFor="drawingNo" className="mb-0 ms-2">
                                            No
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {formData.drawingToBeFollowed && (
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>
                                        UPLOAD DRG
                                        <sup className="mandatory">*</sup>
                                    </label>
                                    {typeof formData.uploadDrg === "string" && formData.uploadDrg ? (
                                        <FilePreview
                                            fileName={formData.uploadDrg.split("/").pop()}
                                            fileUrl={formData.uploadDrg}
                                            onClear={() => handleChange("uploadDrg", null)}
                                        />
                                    ) : (
                                        <>
                                            <input
                                                type="file"
                                                className="form-control form-control-lg mt-2"
                                                accept=".dxf,.pdf,.jpg"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    if (!validateFile(file, ["dxf", "pdf", "jpg"], "DXF, PDF and JPG")) {
                                                        e.target.value = "";
                                                        return;
                                                    }
                                                    handleChange("uploadDrg", file);
                                                }}
                                            />
                                            <small className="text-muted d-block mt-1">
                                                Allowed file type: DXF, PDF and JPG
                                            </small>
                                            <small className="text-muted d-block">
                                                Max File Size: 25MB
                                            </small>
                                            {showError("uploadDrg")}
                                        </>)}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="row mt-4">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>OFF CUT REQUIRED ? <sup className="mandatory">*</sup></label>
                                <div className="d-flex align-items-center mt-2">
                                    <div className="d-flex align-items-center me-4">
                                        <RadioButton
                                            inputId="cutYes"
                                            checked={formData.offCutRequired}
                                            onChange={() =>
                                                handleChange("offCutRequired", true)
                                            }
                                        />
                                        <label htmlFor="cutYes" className="mb-0 ms-2">
                                            Yes
                                        </label>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <RadioButton
                                            inputId="cutNo"
                                            checked={!formData.offCutRequired}
                                            onChange={() =>
                                                handleChange("offCutRequired", false)
                                            }
                                        />
                                        <label htmlFor="cutNo" className="mb-0 ms-2">
                                            No
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body">
                    <h4 className="card-title mb-4">
                        Dispatch Details
                    </h4>
                    <div className="row">
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>
                                    DISPATCH FROM
                                    <sup className="mandatory">*</sup>
                                </label>
                                <Dropdown
                                    value={formData.dispatchFrom}
                                    options={dispatchDDL}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select"
                                    className="w-full md:w-14rem w-100"
                                    onChange={(e) =>
                                        handleChange("dispatchFrom", e.value)
                                    }
                                />
                                {showError("dispatchFrom")}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>
                                    MODE OF TRANSPORT
                                    <sup className="mandatory">*</sup>
                                </label>
                                <Dropdown
                                    value={formData.modeOfTransport}
                                    options={transportDDL}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select"
                                    className="w-full md:w-14rem w-100"
                                    onChange={(e) =>
                                        handleChange("modeOfTransport", e.value)
                                    }
                                />
                                {showError("modeOfTransport")}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>
                                    DISPATCH DOCUMENT
                                    <sup className="mandatory">*</sup>
                                </label>
                                {typeof formData.dispatchDocument === "string" &&
                                    formData.dispatchDocument ? (
                                    <FilePreview
                                        fileName={formData.dispatchDocument.split("/").pop()}
                                        fileUrl={formData.dispatchDocument}
                                        onClear={() => handleChange("dispatchDocument", null)}
                                    />
                                ) : (<>
                                    <input
                                        type="file"
                                        className="form-control form-control-lg"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            if (
                                                !validateFile(
                                                    file,
                                                    ["pdf", "doc", "docx", "jpg", "jpeg", "png", "webp"],
                                                    "PDF, DOC, DOCX, JPG, JPEG, PNG and WEBP"
                                                )
                                            ) {
                                                e.target.value = "";
                                                return;
                                            }
                                            handleChange("dispatchDocument", file);
                                        }}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        Allowed file types: PDF, DOC, DOCX, JPG, JPEG, PNG, WEBP
                                    </small>
                                    <small className="text-muted d-block">
                                        Max File Size: 25MB
                                    </small>
                                    {showError("dispatchDocument")}
                                </>)}
                            </div>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>
                                    CONCERN PERSON NAME
                                    <sup className="mandatory">*</sup>
                                </label>

                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.concernPersonName}
                                    onChange={(e) =>
                                        handleChange("concernPersonName", e.target.value)
                                    }
                                    onKeyDown={(event: any) => {
                                        checkValidInputOnKeyDown(event, ALPHABET_SPACE_PATTERN);
                                        removeSpaceOnFirstCharacter(event);
                                    }}
                                    maxLength={30}
                                />

                                {showError("concernPersonName")}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>
                                    CONTACT NUMBER
                                    <sup className="mandatory">*</sup>
                                </label>

                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.contactNumber}
                                    onKeyDown={(event: any) => {
                                        checkValidInputOnKeyDown(event, NUMBER_ONLY_PATTERN);
                                        removeSpaceOnFirstCharacter(event);
                                    }}
                                    onChange={(e) =>
                                        handleChange("contactNumber", e.target.value)
                                    }
                                    maxLength={10}
                                />

                                {showError("contactNumber")}
                            </div>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-2 mb-2">
                            <div className="form-group">
                                <label>
                                    ZOOM PICTURE
                                </label>

                                {formData.zoomPicture && (
                                    <div className="file-card mt-2 d-flex flex-column align-items-center justify-content-between">
                                        <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                                            <Image
                                                src={formData.zoomPicture}
                                                zoomSrc={formData.zoomPicture}
                                                preview
                                                width="130"
                                                height="100"
                                                imageClassName="file-card-image"
                                                alt="Zoom Picture"
                                            />
                                        </div>

                                        <div
                                            className="file-card-footer text-center text-truncate w-100"
                                            title={formData.zoomPicture.split("/").pop()}
                                        >
                                            {formData.zoomPicture.split("/").pop()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-2 mb-2">
                            <div className="form-group">
                                <label>
                                    PACKING PICTURE
                                </label>

                                {formData.packingPicture && (
                                    <div className="file-card mt-2 d-flex flex-column align-items-center justify-content-between">
                                        <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                                            <Image
                                                src={formData.packingPicture}
                                                zoomSrc={formData.packingPicture}
                                                preview
                                                width="130"
                                                height="100"
                                                imageClassName="file-card-image"
                                                alt="Packing Picture"
                                            />
                                        </div>

                                        <div
                                            className="file-card-footer text-center text-truncate w-100"
                                            title={formData.packingPicture.split("/").pop()}
                                        >
                                            {formData.packingPicture.split("/").pop()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-8 mb-2">
                            <div className="form-group">
                                <label>
                                    REMARKS
                                </label>

                                <InputTextarea
                                    rows={6}
                                    maxLength={500}
                                    className="form-control"
                                    style={{
                                        minHeight: "150px",
                                        resize: "none",
                                    }}
                                    value={formData.remarks}
                                    onChange={(e) =>
                                        handleChange("remarks", e.target.value)
                                    }
                                />

                                {showError("remarks")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-md-12 d-flex align-items-center justify-content-between">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium m-0 me-2"
                        onClick={onBack}
                    >
                        Previous
                    </button>

                    <button
                        type="button"
                        className="btn btn-success btn-md font-weight-medium m-0"
                        onClick={() => {
                            setIsSubmitted(true);
                            setTimeout(() => {
                                handleSubmitStep();
                            }, 100);
                        }}
                    >
                        Save & Next
                    </button>
                </div>
            </div>
        </>
    );
};

export default DispatchDetailsStep;